import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import WorldMap from "@/components/WorldMap";
import { GROWTH_SERVICES, START_OPTIONS, CALENDLY_URL } from "@/content";

const DIFF = [
  ["Where should we focus?", "Which markets and sectors offer the strongest fit for what you do."],
  ["Which companies matter, and why?", "The accounts with a credible reason to buy, prioritised with a rationale."],
  ["Who should we speak to?", "The commercial, operational, procurement and technical people behind the decision."],
  ["What should we do next?", "A practical route from intelligence into real commercial conversations."],
];

export default function Growth() {
  return (
    <div data-testid="growth-page">
      <SEO title="Thrive Growth Adviser | Know where to grow | Thrive Business Development" description="Thrive helps businesses understand markets, identify the companies that matter, find the people behind them and turn commercial intelligence into real business-development opportunities." />

      <section className="relative min-h-[88vh] overflow-hidden pt-36 pb-20 sm:pt-44" data-testid="growth-hero">
        <div className="absolute inset-0 opacity-70"><WorldMap className="h-full w-full" /></div>
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0B0F17_78%)]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6">Global Business Development &amp; Intelligence</p>
            <h1 className="font-display max-w-4xl text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-[-0.035em] leading-[1.02] text-white">Know where to grow.</h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="prose-justify mt-8 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              Thrive helps businesses understand markets, identify the companies that matter, find the people behind them and turn commercial intelligence into real business-development opportunities.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/growth/adviser" data-testid="growth-explore-button" className="group inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow">
                Explore Your Opportunity <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="growth-talk-button" className="group inline-flex items-center gap-2 border border-white/20 px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-gold hover:text-gold">
                Talk to Thrive <ArrowUpRight size={15} />
              </a>
            </div>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Commercial Intelligence + Business Development Execution</p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="growth-start-options">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow mb-6">Where would you like to start?</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] text-white">Where should we grow next, and how should we approach it?</h2>
            </div>
            <Link to="/growth/builder" data-testid="growth-builder-link" className="group inline-flex items-center gap-2 text-sm font-medium text-electric transition-colors hover:text-white">
              Prefer a guided form? Open the Opportunity Builder <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {START_OPTIONS.map(([id, label], i) => (
              <Reveal key={id} delay={i * 0.04} className="h-full">
                <Link to={`/growth/adviser?start=${id}`} data-testid={`growth-start-${id}`} className="group flex h-full flex-col justify-between gap-10 bg-navy p-7 transition-colors duration-300 hover:bg-navy-light/60">
                  <span className="font-mono text-xs text-gold/70">0{i + 1}</span>
                  <span className="flex items-end justify-between gap-4">
                    <span className="font-display text-lg font-bold tracking-[-0.03em] leading-snug text-white">{label}</span>
                    <ArrowUpRight size={18} className="shrink-0 text-slate-600 transition-all duration-300 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="growth-difference">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
          <Reveal>
            <p className="eyebrow-blue mb-6">The Thrive Difference</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] leading-tight text-white">Intelligence that leads to action.</h2>
            <p className="prose-justify mt-6 max-w-md text-base text-slate-400 leading-relaxed">Thrive is not a recruitment company, a marketing agency, a database reseller or a report-only consultancy. We combine commercial intelligence with business development execution, supporting businesses across global markets.</p>
          </Reveal>
          <div className="divide-y divide-white/10 border-y border-white/10">
            {DIFF.map(([q, a], i) => (
              <Reveal key={q} delay={i * 0.06}>
                <div className="grid gap-2 py-6 sm:grid-cols-[40px_1fr]">
                  <span className="font-mono text-xs text-gold/70">0{i + 1}</span>
                  <div><p className="font-display text-lg font-bold tracking-[-0.03em] text-white">{q}</p><p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{a}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="growth-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><p className="eyebrow mb-6">How Thrive Supports</p><h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] text-white">From market intelligence to real business development.</h2></Reveal>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
            {GROWTH_SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.04} className="h-full">
                <div className="flex h-full flex-col bg-navy p-7" data-testid={`growth-service-${s.id}`}>
                  <span className="font-mono text-xs text-gold/70">0{i + 1}</span>
                  <h3 className="mt-6 font-display text-lg font-bold tracking-[-0.03em] leading-snug text-white">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.body}</p>
                  <p className="mt-auto border-t border-white/10 pt-4 text-xs leading-relaxed text-electric/85"><span className="font-mono uppercase tracking-[0.2em] text-slate-500">Outcome </span>{s.outcome}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="growth-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <Reveal>
            <h2 className="font-display max-w-xl text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">Find the market. Find the people. Build the opportunity.</h2>
            <p className="mt-4 max-w-xl text-base text-slate-400 leading-relaxed">Tell us the market, company or growth challenge you're trying to understand. We'll show you how we can help.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4">
              <Link to="/growth/adviser" data-testid="growth-cta-adviser" className="group inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all hover:bg-gold-glow">Explore Your Opportunity <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></Link>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="growth-cta-talk" className="inline-flex items-center gap-2 border border-white/20 px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:border-gold hover:text-gold">Talk to Thrive <ArrowUpRight size={15} /></a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
